import React from "react";
import { connect } from "react-redux";
import {
    currentOrganization,
    userOrganizations
} from "./selectors/user.selector";
import { currentSalesOrderId } from "./selectors/data.selector";
import EntityActions from "./actions";
import EntitySelectors from "./selectors";
import UserActions from "./actions/user.actions";
import { canApplyOrgOnCurrentSpa } from "../utils/SsoColdStart";

let salesOrderActions = EntityActions.salesOrder;
let syncActions = EntityActions.sync;
let syncSelectors = EntitySelectors.sync;

/**
 * Copies entity ids from the route into Redux when they differ from stored values.
 * Covers salesOrderId and syncId path segments. The literal "new" is the create-page
 * route token, not an entity id, so it is ignored.
 */
function syncEntityIdsFromUrl(
    { salesOrderId, syncId },
    { reduxSalesOrderId, reduxSyncId }
) {
    if (salesOrderId && salesOrderId !== "new") {
        if (!reduxSalesOrderId || salesOrderId != reduxSalesOrderId) {
            salesOrderActions.setCurrentId(salesOrderId);
        }
    }

    if (syncId) {
        if (!reduxSyncId || syncId != reduxSyncId) {
            syncActions.setCurrentId(syncId);
        }
    }
}

/**
 * HOC that keeps the URL and Redux aligned for organization, sales order, and sync context.
 *
 * Organization sync runs in lifecycle methods (async, may await SSO). Entity id sync runs
 * in mapStateToProps (synchronous) so child components always see ids that match the route.
 */
export function withUrlSync(ComposedComponent) {
    class ReduxUrlSync extends React.Component {
        // Set while setCurrentOrganization is awaiting SSO approval.
        _orgSwitchInFlight = false;
        // Set when the URL changes again before the in-flight org switch completes.
        _pendingUrlSync = false;
        // orgId from the URL that SSO rejected; skipped until the user navigates elsewhere.
        _ssoDeniedUrlOrgId = null;

        // Hard-nav in progress after ensureOrgProfile; avoid duplicate profile redirects.
        _profileRedirectInFlight = false;

        componentDidMount() {
            // Initial pass: entity ids from the route, then organization from the URL.
            syncEntityIdsFromUrl(this.props.match.params, this.props);
            this._syncOrgFromUrl(this.props);
        }

        componentDidUpdate(prevProps) {
            const prevParams = prevProps.match.params;
            const nextParams = this.props.match.params;
            const routeOrgChanged = prevParams.orgId !== nextParams.orgId;

            if (routeOrgChanged) {
                // New route org — allow SSO to be attempted again for this orgId.
                this._ssoDeniedUrlOrgId = null;
            }

            // Deep links can mount before the user's organization list has loaded.
            const userOrgsBecameReady =
                !(prevProps.userOrgs && prevProps.userOrgs.length) &&
                this.props.userOrgs &&
                this.props.userOrgs.length > 0;

            // Reconcile org when the route orgId changes or orgs first become available.
            // salesOrderId and syncId are updated in mapStateToProps instead.
            // Redux currentOrg changes alone do not trigger sync — only the URL orgId drives it.
            if (routeOrgChanged || userOrgsBecameReady) {
                this._syncOrgFromUrl(this.props);
            }
        }

        /**
         * Sets Redux current organization to match the URL orgId.
         *
         * Validates access (SSO), signs out when the user has no orgs, and redirects when
         * the requested orgId is not in the user's list. Runs outside mapStateToProps because
         * it is async and must not run on every Redux state change.
         */
        async _syncOrgFromUrl(props) {
            const { orgId } = props.match.params;
            const { userOrgs, currentOrg } = props;

            if (!orgId || !userOrgs) return;

            if (userOrgs && userOrgs.length == 0) {
                UserActions.signOut();
                return;
            }

            if (!isNaN(orgId) && (!currentOrg || currentOrg.id != orgId)) {
                if (this._ssoDeniedUrlOrgId == String(orgId)) {
                    return;
                }

                if (this._orgSwitchInFlight) {
                    this._pendingUrlSync = true;
                    return;
                }

                if (this._profileRedirectInFlight) {
                    return;
                }

                let org = userOrgs.find((o) => o.id == orgId) || userOrgs[0];
                if (org) {
                    this._orgSwitchInFlight = true;
                    try {
                        // REV-17039: SSO + cross-profile — profile hop before setCurrentOrganization.
                        if (!(await canApplyOrgOnCurrentSpa(org))) {
                            this._profileRedirectInFlight = true;
                            return;
                        }

                        const orgSwitchAllowed =
                            await UserActions.setCurrentOrganization(org);
                        if (!orgSwitchAllowed) {
                            this._ssoDeniedUrlOrgId = String(orgId);
                            return;
                        }
                        this._ssoDeniedUrlOrgId = null;

                        if (orgId && org.id != orgId) {
                            // URL orgId is not in the user's org list; fall back to their default org.
                            if (window.location.pathname != `/${org.id}`) {
                                props.history.push("/");
                            }
                        }
                    } finally {
                        this._orgSwitchInFlight = false;
                        if (this._pendingUrlSync) {
                            this._pendingUrlSync = false;
                            this._syncOrgFromUrl(this.props);
                        }
                    }
                } else {
                    // User has no accessible organization.
                    UserActions.signOut();
                }
            }
        }

        render() {
            return (
                <>
                    <ComposedComponent {...this.props} />
                </>
            );
        }
    }

    function mapStateToProps(state, props) {
        const { orgId } = props.match.params;
        const userOrgs = userOrganizations(state);
        const reduxSalesOrderId = currentSalesOrderId(state);
        const reduxSyncId = syncSelectors.currentId(state);

        // Keep sales order and sync ids in Redux aligned with the route before children render.
        if (orgId && userOrgs) {
            syncEntityIdsFromUrl(props.match.params, {
                reduxSalesOrderId: reduxSalesOrderId,
                reduxSyncId: reduxSyncId
            });
        }

        return {
            currentOrg: currentOrganization(state),
            userOrgs: userOrgs,
            reduxSalesOrderId: reduxSalesOrderId,
            reduxSyncId: reduxSyncId
        };
    }

    return connect(mapStateToProps)(ReduxUrlSync);
}
